import { useCallback, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import CloseIcon from '@/material-icons/400-24px/close.svg?react';
import {
  changeComposing,
  mountCompose,
  unmountCompose,
} from 'mastodon/actions/compose';
import { Icon } from 'mastodon/components/icon';
import ServerBanner from 'mastodon/components/server_banner';
import { Search } from 'mastodon/features/compose/components/search';
import ComposeFormContainer from 'mastodon/features/compose/containers/compose_form_container';
import { SignInBanner } from 'mastodon/features/navigation_panel/components/sign_in_banner';
import { Trends } from 'mastodon/features/navigation_panel/components/trends';
import { LinkFooter } from 'mastodon/features/ui/components/link_footer';
import { useIdentity } from 'mastodon/identity_context';
import { useAppDispatch } from 'mastodon/store';

import { FollowFeed } from './follow_feed';

export type RightPaneMode = 'following' | 'compose';

const messages = defineMessages({
  label: { id: 'threepane.right_pane', defaultMessage: 'Following and composer' },
  following: { id: 'threepane.following', defaultMessage: 'Following' },
  composing: { id: 'threepane.composing', defaultMessage: 'Composing' },
  cancel: { id: 'threepane.cancel_compose', defaultMessage: 'Discard' },
  close: { id: 'threepane.close_pane', defaultMessage: 'Close' },
});

/**
 * Right column. Top: search. Below: either the compact "Following" list or the composer.
 * The composer stays mounted in both modes, so a draft survives switching tabs and the
 * stock "reply / mention / edit" actions always find it (they open /publish otherwise).
 */
export const RightPane: React.FC<{
  mode: RightPaneMode;
  onModeChange: (mode: RightPaneMode) => void;
  onDiscard: () => void;
  onClose?: () => void;
  dirty: boolean;
}> = ({ mode, onModeChange, onDiscard, onClose, dirty }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { signedIn } = useIdentity();

  useEffect(() => {
    if (!signedIn) {
      return undefined;
    }

    dispatch(mountCompose());

    return () => {
      dispatch(unmountCompose());
    };
  }, [dispatch, signedIn]);

  const handleFocus = useCallback(() => {
    dispatch(changeComposing(true));
  }, [dispatch]);

  const showFollowing = useCallback(() => {
    onModeChange('following');
  }, [onModeChange]);
  const showComposing = useCallback(() => {
    onModeChange('compose');
  }, [onModeChange]);

  return (
    <div
      className='threepane__right-inner'
      role='complementary'
      aria-label={intl.formatMessage(messages.label)}
    >
      <div className='threepane__right-top'>
        <div className='threepane__search'>
          <Search singleColumn />
        </div>
        {onClose && (
          <button
            type='button'
            className='threepane__right-close'
            onClick={onClose}
            title={intl.formatMessage(messages.close)}
            aria-label={intl.formatMessage(messages.close)}
          >
            <Icon id='times' icon={CloseIcon} />
          </button>
        )}
      </div>

      {signedIn ? (
        <>
          <div className='threepane__tabs' role='tablist'>
            <button
              type='button'
              role='tab'
              aria-selected={mode === 'following'}
              className={classNames('threepane__tab', {
                active: mode === 'following',
              })}
              onClick={showFollowing}
            >
              {intl.formatMessage(messages.following)}
            </button>
            <button
              type='button'
              role='tab'
              aria-selected={mode === 'compose'}
              className={classNames('threepane__tab', {
                active: mode === 'compose',
                'has-draft': dirty,
              })}
              onClick={showComposing}
            >
              {intl.formatMessage(messages.composing)}
            </button>
            {mode === 'compose' && (
              <button
                type='button'
                className='threepane__tab-action'
                onClick={onDiscard}
              >
                {intl.formatMessage(messages.cancel)}
              </button>
            )}
          </div>

          <div
            className='threepane__right-body threepane__right-body--following'
            hidden={mode !== 'following'}
          >
            {mode === 'following' && <FollowFeed />}
          </div>

          <div
            className='threepane__right-body threepane__right-body--compose compose-panel'
            hidden={mode !== 'compose'}
            onFocus={handleFocus}
          >
            <ComposeFormContainer singleColumn />
          </div>
        </>
      ) : (
        <div className='threepane__right-body threepane__right-body--guest'>
          <SignInBanner />
          <Trends />
          <ServerBanner />
          <LinkFooter context='multi-column' />
        </div>
      )}
    </div>
  );
};
